<template>
	<div class="el-pagination">
		<!-- 总条数 -->
		<div class="el-pagination__total">共{{total}}条</div>
		<!-- 上一页按钮 -->
		<button class="el-pagination__btn prev" :disabled="currentPage === 1 || disabled" @click="handlePrev"
			v-if="layout.includes('prev')">
			<img src="../assets/Home/prevPage.png" />
		</button>

		<div class="el-pager" v-if="layout.includes('pager')">
			<!-- 首页 -->
			<span v-if="showFirstPage && currentPage > 4" class="number" @click="goToPage(1)">
				1
			</span>

			<!-- 前面的省略号 -->
			<span class="el-pagination__ellipsis" v-if="showPrevMore">
				...
			</span>

			<!-- 页码按钮 -->
			<span v-for="pager in pagers" :key="pager" :class="{
          'number': true,
          'active': pager === currentPage
        }" @click="goToPage(pager)">
				{{ pager }}
			</span>

			<!-- 后面的省略号 -->
			<span class="el-pagination__ellipsis" v-if="showNextMore">
				...
			</span>

			<!-- 末页 -->
			<span v-if="showLastPage && currentPage < totalPages - 3" class="number" @click="goToPage(totalPages)">
				{{ totalPages }}
			</span>
		</div>

		<!-- 下一页按钮 -->
		<button class="el-pagination__btn next" :disabled="currentPage === totalPages || disabled" @click="handleNext"
			v-if="layout.includes('next')">
			<img src="../assets/Home/nextPage.png" />
		</button>

		<div class="el-pagination__jump">
			<div class="jumpTo">跳至</div>
			<input type="number" v-model="internalCurrentPage" @change="handleCurrentChange"
				@keydown.enter="handleCurrentChange" :min="1" :max="totalPages" class="el-pagination__jump-input">
			<div class="jumpTo">页</div>
		</div>

		<!-- 合计信息 -->
		<slot name="total" v-if="showTotal && $slots.total"></slot>
	</div>
</template>

<script>
	export default {
		name: 'PagePagination',
		props: {
			// 当前页码
			currentPage: {
				type: Number,
				default: 1
			},
			// 总条数
			total: {
				type: Number,
				default: 0
			},
			// 每页显示条数
			pageSize: {
				type: Number,
				default: 10
			},
			// 页码按钮的数量，当总页数超过该值时会折叠
			pagerCount: {
				type: Number,
				default: 7,
				validator(val) {
					return val > 0 && val < 21 && val % 2 === 1
				}
			},
			// 布局，子组件名用逗号分隔
			layout: {
				type: String,
				default: 'total, sizes, prev, pager, next, jumper'
			},
			// 禁用分页
			disabled: {
				type: Boolean,
				default: false
			},
			// 是否显示总数
			showTotal: {
				type: Boolean,
				default: false
			},
		},
		data() {
			return {
				internalCurrentPage: this.currentPage,
				internalPageSize: this.pageSize
			}
		},
		computed: {
			// 总页数
			totalPages() {
				return Math.max(1, Math.ceil(this.total / this.internalPageSize))
			},
			// 计算显示的页码
			pagers() {
				const pagerCount = this.pagerCount
				const half = Math.floor(pagerCount / 2)
				let start = Math.max(1, this.currentPage - half)
				let end = start + pagerCount - 1

				// 调整页码范围
				if (end > this.totalPages) {
					end = this.totalPages
					start = Math.max(1, end - pagerCount + 1)
				}

				const pagers = []
				for (let i = start; i <= end; i++) {
					pagers.push(i)
				}
				return pagers
			},
			// 是否显示首页
			showFirstPage() {
				return this.totalPages > this.pagerCount
			},
			// 是否显示末页
			showLastPage() {
				return this.totalPages > this.pagerCount
			},
			// 是否显示前省略号
			showPrevMore() {
				return this.currentPage - 4 > 1
			},
			// 是否显示后省略号
			showNextMore() {
				return this.currentPage + 3 < this.totalPages
			}
		},
		watch: {
			currentPage(newVal) {
				this.internalCurrentPage = newVal
			},
			pageSize(newVal) {
				this.internalPageSize = newVal
			}
		},
		methods: {
			// 跳转到指定页
			goToPage(page) {
				if (page !== this.currentPage && page >= 1 && page <= this.totalPages && !this.disabled) {
					this.internalCurrentPage = page
					this.$emit('current-change', page)
				}
			},
			// 上一页
			handlePrev() {
				if (this.currentPage > 1 && !this.disabled) {
					const newPage = this.currentPage - 1
					this.internalCurrentPage = newPage
					this.$emit('current-change', newPage)
				}
			},
			// 下一页
			handleNext() {
				if (this.currentPage < this.totalPages && !this.disabled) {
					const newPage = this.currentPage + 1
					this.internalCurrentPage = newPage
					this.$emit('current-change', newPage)
				}
			},
			// 改变当前页码
			handleCurrentChange() {
				let newPage = parseInt(this.internalCurrentPage)
				if (isNaN(newPage) || newPage < 1) {
					newPage = 1
				} else if (newPage > this.totalPages) {
					newPage = this.totalPages
				}
				this.internalCurrentPage = newPage
				this.goToPage(newPage)
			}
		}
	}
</script>

<style scoped>
	.el-pagination {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: .6rem 0 .8rem;
		font-size: 14px;
	}

	.el-pagination__total {
		margin-right: .16rem;
		font-weight: 400;
		font-size: .14rem;
		color: rgba(0, 0, 0, 0.6);
	}

	.el-pagination__jump {
		display: flex;
		align-items: center;
		margin-left: .16rem;
		font-weight: 400;
		font-size: .14rem;
		color: rgba(0, 0, 0, 0.9);
	}

	.jumpTo {
		height: .32rem;
		min-width: unset;
		font-weight: 400;
		font-size: .14rem;
		line-height: .32rem;
	}

	.el-pagination__jump-input {
		width: .6rem;
		height: .32rem;
		border: 0.01rem solid #dcdfe6;
		border-radius: .04rem;
		padding: .04rem;
		margin: 0 .04rem;
		text-align: center;
	}

	.el-pager {
		display: inline-flex;
		align-items: center;
	}

	.el-pager .number {
		width: .32rem;
		height: .32rem;
		min-width: unset;
		font-weight: 400;
		font-size: .14rem;
		color: rgba(0, 0, 0, 0.6);
		line-height: .32rem;
		text-align: center;
		border: .01rem solid #DCDCDC;
		margin: 0 .04rem;
		border-radius: 4px;
		cursor: pointer;
		transition: all 0.3s;
	}

	.el-pager .number:hover {
		border-color: #E29358;
		color: #E29358;
	}

	.el-pager .number.active {
		background: #E29358;
		border-color: #E29358;
		color: #252525;
	}

	.el-pagination__ellipsis {
		margin: 0 4px;
		width: .32rem;
		height: .32rem;
		font-weight: 400;
		font-size: .14rem;
		color: rgba(0, 0, 0, 0.6);
		text-align: center;
	}

	.el-pagination__btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		height: .32rem;
		width: .32rem;
		min-width: unset;
		border: .01rem solid #DCDCDC;
		border-radius: .04rem;
		background-color: #FFFFFF;
		padding: 0 0;
		cursor: pointer;
		transition: all 0.3s;

		img {
			width: .16rem;
			height: .16rem;
		}
	}

	.el-pagination__btn:hover:not(:disabled) {
		border-color: #E29358;
		color: #E29358;
	}

	.el-pagination__btn:disabled {
		color: #c0c4cc;
		cursor: not-allowed;
		background-color: #f5f7fa;
	}

	.el-pagination__btn.prev {
		margin-right: .04rem;
	}

	.el-pagination__btn.next {
		margin-left: .04rem;
	}
</style>